'use client'

import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { WizardPanel } from '@repo/ui/WizardPanel'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { useNewClientStore } from '@repo/store/useNewClientStore'
import { useWizardStore } from '@repo/store/useWizardStore'
import { useClientStore } from '@repo/store/useClientStore'
import MUITypography from '@mui/material/Typography'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIStack from '@mui/material/Stack'
import MUIAlert from '@mui/material/Alert'
import { useIntersectionObserver } from '@uidotdev/usehooks'
import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import { LoadingState } from '@repo/ui/LoadingState'

export const NewClientCheckExisting = ({ wideMode, handlePrevNext, testMode = false }) => {
  const { namedClients, updateState, newClientNo } = useNewClientStore()
  const { checkforSimilarClients } = useClientStore()
  const { setWizardStateInBulk } = useWizardStore()
  const [similarClients, setSimilarClients] = useState([])
  const [selectedClientIndex, setSelectedClientIndex] = useState(-1)
  const [totalResults, setTotalResults] = useState(0)
  const [gotResults, setGotResults] = useState(false)
  const [startAtIndex, setStartAtIndex] = useState(0)
  const pageSize = 20

  // the intersection observer is used to load more results when the user scrolls to the bottom of the list
  const [ref, entry] = useIntersectionObserver({
    threshold: 0,
    root: null,
    rootMargin: '0px'
  })

  useEffect(() => {
    if (entry?.isIntersecting) {
      handlePagination()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry])

  const handleClickContinue = () => {
    // proceeding with the new client
    if (selectedClientIndex === -1) {
      setWizardStateInBulk([
        {
          key: `newclient-check-existing-clients`,
          value: { status: 'completed', statusText: 'Proceeding with new client' }
        },
        {
          key: `newclient-phone-numbers`,
          value: { status: 'todo', statusText: '' }
        },
        {
          key: `newclient-client-account-control-details`,
          value: { status: 'todo', statusText: '' }
        }
      ])

      updateState({ key: 'newClientNo', value: newClientNo })
      updateState({ key: 'isNew', value: true })
      updateState({ key: 'existingClient', value: null })
      handlePrevNext(1)
    } else {
      // proceeding with a selected client that already exists
      const selectedClientDetails = similarClients[selectedClientIndex] || null
      if (!selectedClientDetails) return

      setWizardStateInBulk([
        {
          key: `newclient-check-existing-clients`,
          value: { status: 'completed', statusText: 'Selected an existing client' }
        },
        {
          key: `newclient-phone-numbers`,
          value: { status: 'skipped', statusText: 'N/A' }
        },
        {
          key: `newclient-client-account-control-details`,
          value: { status: 'skipped', statusText: 'N/A' }
        }
      ])

      updateState({ key: 'isNew', value: false })
      updateState({ key: 'newClientNo', value: null })
      updateState({ key: 'existingClient', value: selectedClientDetails })
      handlePrevNext(3)
    }
  }

  const handleClickBack = () => {
    handlePrevNext(-1)
  }

  useEffect(() => {
    if (!namedClients?.length) return

    const hasMoreResults = !gotResults || startAtIndex + pageSize <= totalResults
    if (!hasMoreResults) return

    const fetchSimilarClients = async () => {
      const res = await checkforSimilarClients({ ...namedClients[0], startAtIndex, pageSize })
      setSimilarClients([...similarClients, ...res.rows])
      setTotalResults(res.totalRowCount)
      setGotResults(true)
    }

    if (!testMode) fetchSimilarClients()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namedClients, startAtIndex])

  const handleSelection = (val) => {
    setSelectedClientIndex(Number(val))
  }

  const handlePagination = () => {
    const hasMoreResults = !gotResults || startAtIndex + pageSize <= totalResults
    if (!hasMoreResults) return

    setStartAtIndex(startAtIndex + pageSize)
  }

  const count = similarClients?.length || 0
  const title = ` ${totalResults === 0 ? `Great! We've checked and` : 'Warning:'} there ${totalResults === 1 ? 'is' : 'are'} ${totalResults ? totalResults : 'no'} existing client${totalResults === 1 ? '' : 's'} with similar details`

  return (
    <div data-testid="NewClientCheckExisting">
      {gotResults ? (
        <WizardPanel sx={{ padding: '40px 40px' }}>
          <MUIAlert
            severity={totalResults === 0 ? 'success' : 'error'}
            sx={{
              mt: 2,
              padding: '16px 36px',
              svg: { width: '28px', height: '28px', ml: '-4px' },
              '.MuiAlert-message': { mt: '4px', fontSize: '1rem', ml: '-4px', lineHeight: 1.5 }
            }}
          >
            {title}
          </MUIAlert>

          <MUIRadioGroup
            value={selectedClientIndex}
            name="selectedClient"
            sx={{ width: '100%' }}
            onChange={(e) => handleSelection(e.target.value)}
          >
            <RadioButton
              value={-1}
              label={`Continue ${count ? 'anyway ' : ''}and create a new client`}
              selectedClientIndex={selectedClientIndex}
            />

            {totalResults ? (
              <MUITypography variant="h5" sx={{ mt: 12, ml: 10 }}>
                {totalResults === 1
                  ? 'Or proceed with the following client:'
                  : 'Or proceed with one of the following clients:'}
              </MUITypography>
            ) : null}

            {similarClients && similarClients.length
              ? similarClients.map(({ clientNo, forename, surname, emailAddress }, index) => {
                  return (
                    <RadioButton
                      key={`${clientNo}-${forename}-${surname}-${index}`}
                      value={index}
                      label={`${forename} ${surname}`}
                      selectedClientIndex={selectedClientIndex}
                      showClientDetails
                      clientNo={clientNo}
                      emailAddress={emailAddress}
                    />
                  )
                })
              : null}
          </MUIRadioGroup>

          {totalResults > startAtIndex + pageSize ? <div ref={ref} /> : null}
        </WizardPanel>
      ) : (
        <MUIStack sx={{ mt: 40, width: '100%' }} justifyContent="center" direction="row">
          <LoadingState />
        </MUIStack>
      )}
      <WizardFooter wideMode={wideMode} handleClickContinue={handleClickContinue} handleClickBack={handleClickBack} />
    </div>
  )
}

NewClientCheckExisting.propTypes = {
  updateStepState: PropTypes.func,
  currentStep: PropTypes.object,
  wideMode: PropTypes.bool,
  handlePrevNext: PropTypes.func,
  wizardId: PropTypes.string,
  testMode: PropTypes.bool
}

const RadioButton = ({ value, label, selectedClientIndex, showClientDetails = false, clientNo, emailAddress }) => {
  const isSelected = value === selectedClientIndex
  let content = label

  if (showClientDetails) {
    content = (
      <MUIStack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: '100%' }}
        gap={2}
        flexWrap={'wrap'}
      >
        <MUIStack direction="column" alignItems="flex-center" justifyContent="center" sx={{ flex: 1 }} gap={2}>
          <MUITypography variant="body1" sx={{ mb: 0, userSelect: 'none', cursor: 'pointer' }} noWrap>
            {label}
          </MUITypography>
          {emailAddress ? (
            <MUITypography
              variant="body1"
              sx={{ mt: -2, mb: 0, color: 'text.secondary', userSelect: 'none', cursor: 'pointer' }}
              noWrap
            >
              {emailAddress}
            </MUITypography>
          ) : null}
        </MUIStack>
        <ClientMatterDetailsPill clientNo={clientNo} sx={{ marginTop: '4px' }} />
      </MUIStack>
    )
  }

  return (
    <MUIStack
      gap={2}
      sx={{
        mt: 4,
        mr: 10,
        ml: 10,
        width: 'calc(100% - 80px)',
        backgroundColor: isSelected ? 'background.slate100' : 'background.paper',
        borderRadius: 1,
        '&:hover': {
          backgroundColor: isSelected ? 'background.slate100' : 'background.dark'
        }
      }}
    >
      <MUIFormControlLabel
        value={value}
        control={<MUIRadio />}
        label={content}
        sx={{ padding: '10px 24px', mr: 0, ml: 0, width: '100%', '.MuiFormControlLabel-label': { width: '100%' } }}
      />
    </MUIStack>
  )
}

RadioButton.propTypes = {
  value: PropTypes.number,
  label: PropTypes.string,
  selectedClientIndex: PropTypes.any,
  index: PropTypes.number,
  showClientDetails: PropTypes.bool,
  clientNo: PropTypes.string,
  emailAddress: PropTypes.string
}
